










// Vue
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Mixins } from "vue-mixin-decorator";
import { mapGetters, mapActions } from "vuex";

// Components
import Dropdown, {
  DropdownWidth
} from "@/shared/components/Dropdown.component.vue";
import EmptyState from "@/shared/components/empty-state.component.vue";
import Pagination from "@/shared/components/Pagination/Pagination.component.vue";
import Search, {
  SearchPredefinedStyle
} from "@/shared/components/search.component.vue";
import Spinner from "@/shared/components/Spinner.component.vue";
import ButtonSwitch, {
  SwitchActiveColor,
  ButtonSwitchPredefinedStyle
} from "@/shared/components/button-switch.component.vue";
import ItemThumb, {
  ItemThumbPrefeginedStyle
} from "@/shared/components/item-thumb.component.vue";
import Btn, { BtnColor, BtnWidth } from "@/shared/components/btn.component.vue";
// Views
import ConvocationCard, {
  CardNumber
} from "@/shared/components/convocation-card/convocation-card.component.vue";

// Mixins
import SearchMixin from "@/shared/mixins/search.mixin.ts";
import WithPagination from "@/shared/mixins/withPagination.mixin.ts";
import SettingsMixin from "@/shared/mixins/settings.mixin.ts";

// Constants
import Assets from "@/constants/assets";
import Constants from "@/constants/constants";
import RouteNames from "@/constants/route-names";

// Models
import { TypeOfProduct } from "@/manager/product/models/product.model";

// Filters
import pluralizeFilter from "@/shared/filters/pluralize.filter.ts";

// Services
import UtilsService from "@/core/services/utils.service";

// Third party
import dayjs from "dayjs";
import "dayjs/locale/es";

interface MixinInterface extends WithPagination, SearchMixin, SettingsMixin {
}

@Component({
  name: "cb-ConvocationList",
  components: {
    Dropdown,
    ConvocationCard,
    ButtonSwitch,
    EmptyState,
    Pagination,
    Search,
    Spinner,
    ItemThumb,
    Btn
  },
  filters: {
    pluralizeFilter
  },
  computed: {
    ...mapGetters({
      convocationList: "getConvocationsList"
    })
  }
})
export default class ConvocationList extends Mixins<MixinInterface>(
    WithPagination,
    SearchMixin,
    SettingsMixin
) {
  private assets = Assets;
  private constants = Constants;
  private convocationList!: any;
  private convocationListLoaded = false;
  private initialLoading = true;
  private cleanSearch = false;
  private timeSwitchSelected = {};
  private ordering?: string;
  private itemThumbPrefeginedStyle = ItemThumbPrefeginedStyle;
  private searchPredefinedStyle = SearchPredefinedStyle;
  private buttonSwitchPredefinedStyle = ButtonSwitchPredefinedStyle;
  private dateFrom: any = null;
  private dateTo: any = null;
  private utilsService = UtilsService;
  private productType?: string = this.constants.switchConference.requestName;
  private productTypeSelected = this.constants.switchCourse;
  private dropdownWidth = DropdownWidth;
  private btnWidth = BtnWidth;
  private btnColor = BtnColor;

  @Watch("productTypeSelected")
  private watchProductTypeSelected(newVal: any, oldVal: any) {
    this.productType = newVal.requestName;
    this.pushQueryStrings();
  }

  private created() {
    this.initializePagination();
    this.initializeSettings();
    window.setTimeout(() => {
      // this.fetchFirstPageList();
      this.initialLoading = false;
    }, 0);
  }

  get productTypeOptions(): any[] {
    return [
      this.constants.switchConference,
      this.constants.switchCourse,
      this.constants.switchEvent
    ];
  }

  get showPagination() {
    return (
        this.convocationListLoaded &&
        !this.spinner.show &&
        this.convocationList.length > 0
    );
  }

  get showEmptyState() {
    return (
        this.convocationListLoaded &&
        !this.spinner.show &&
        this.convocationList.length === 0
    );
  }

  get timeFilter(): any[] {
    return [this.constants.switchPast, this.constants.switchFuture];
  }

  public fetchList() {
    this.changeSpinnerVisibility(true);
    this.fetchConvocationList((res: any) => {
      this.onFetchSuccess(res);
      this.convocationListLoaded = true;
      this.changeSpinnerVisibility(false);
    });
  }

  public searchCallback() {
    this.pushQueryStrings();
  }

  public callbackRouteQueryChange() {
    this.getAllQueryParams();
    this.fetchFirstPageList();
  }

  public handleComeBack() {
    this.cleanSearch = !this.cleanSearch;
    this.searchString = "";
  }

  private getAllQueryParams() {
    this.getProductTypeParam();
    this.getTimeSwitchParam();
    this.getSearchParam();
  }

  private goToOfficeSales() {
    const convocation = {
      id: this.constants.SpecialAnnouncementId,
      product: {
        typeOfProduct: "specialAnnouncement"
      }
    };
    const url = this.routeConvocationCard(convocation);
    this.$router.push(url);
  }

  private getProductTypeParam() {
    this.productType = this.constants.switchConference.requestName;
    const routeValue = this.$route.query.product_type;
    if (routeValue) {
      this.productTypeSelected = this.productTypeOptions.find((option: any) => {
        return option.requestName === routeValue;
      });
      this.productType = this.productTypeSelected.requestName;
    }
  }

  private getTimeSwitchParam() {
    const from = this.$route.query.from;
    const to = this.$route.query.to;
    if (from) {
      this.dateFrom = from;
      this.timeSwitchSelected = this.constants.switchFuture;
    }
    if (to) {
      this.dateTo = to;
      this.timeSwitchSelected = this.constants.switchPast;
      this.ordering = "-initial_date";
    }
  }

  private getSearchParam() {
    const search = this.$route.query.search;
    this.searchString = search;
  }

  private initializePagination() {
    this.itemsPerPage = 25;
    this.limit = this.itemsPerPage;
  }

  private initializeSettings() {
    this.initialize(RouteNames.cbConvocationList);
  }

  private pushQueryStrings() {
    const productType = this.productType || "";
    const to = this.dateTo;
    const from = this.dateFrom;
    const search = this.searchString;
    this.$router.push({
      name: this.$route.name,
      query: {
        product_type: productType,
        to,
        from,
        search
      }
    });
  }

  private timeFilterChanged(option: any) {
    this.dateFrom = null;
    this.dateTo = null;
    this.ordering = undefined;
    if (option.id === this.constants.switchPast.id) {
      this.dateTo = this.utilsService.yesterday();
      this.ordering = "-initial_date";
      this.timeSwitchSelected = this.constants.switchPast;
    } else if (option.id === this.constants.switchFuture.id) {
      this.dateFrom = this.utilsService.today();
      this.timeSwitchSelected = this.constants.switchFuture;
    }
    this.pushQueryStrings();
  }

  private fetchConvocationList(
      callback: any = () => {
        const doNothing = 1;
      }
  ) {
    const payload = {
      queryParams: {
        limit: this.limit,
        offset: this.offset,
        search: this.searchString,
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        ordering: this.ordering,
        productType: this.productType
      }
    };
    this.$store.dispatch("fetchConvocationList", payload).then(res => {
      callback(res);
    });
  }

  private convocationCardNumber(index: number) {
    let cardClass = CardNumber.Even;
    if (index % 2 === 1) {
      cardClass = CardNumber.Odd;
    }

    return cardClass;
  }

  private routeConvocationCard(convocation: any) {
    const route = {
      name: RouteNames.cbPariticipantList,
      query: {},
      params: {
        id: convocation.id
      }
    };

    // if (convocation.product.typeOfProduct === TypeOfProduct.Course) {
    //   route.name = RouteNames.cbProductCategory;
    // }

    if (convocation.product.typeOfProduct === TypeOfProduct.Course) {
      const routeCourse = {
        name: RouteNames.cbSales,
        query: { ...this.$route.query, "type-of-product": TypeOfProduct.Course },
        params: {
          convocationId: convocation.id,
          productTypeId: TypeOfProduct[TypeOfProduct.Course].toLowerCase()
        }
      };
      return routeCourse
    }

    if (convocation.product.typeOfProduct === "specialAnnouncement") {
      route.name = RouteNames.cbProductCategory;
      route.query = { specialAnnouncement: true };
    }

    return route;
  }

  private logout() {
    this.$store.dispatch("authLogout").then(() => {
      this.$router.push("/login");
    });
  }
}
